import { useAuth as useClerkAuth, useSession, useUser } from '@clerk/react'
import { useSignIn, useSignUp } from '@clerk/react/legacy'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { clearStellaSessionStorage } from '@/hooks/useStellaSession'
import { useToast } from '@/hooks/useToast'
import '@/hooks/usePostHogTracking'
import type { OrganizationMetadata } from '@/components/organizations/details/utils'
import { linkAnonymousConsentLogsToUser } from '@/services/CookieConsentService'
import { postHogTrackingService } from '@/services/PostHogTrackingService'
import { referralAttributionService } from '@/services/ReferralAttributionService'

export interface AuthUser {
  id: string
  firstName?: string | null
  lastName?: string | null
  primaryEmailAddress?: {
    emailAddress: string
  } | null
  imageUrl?: string | null
  hasImage?: boolean
  reload?: () => Promise<unknown>
  updatePassword?: (params: {
    currentPassword: string
    newPassword: string
    signOutOfOtherSessions: boolean
  }) => Promise<unknown>
  createdAt: Date
  publicMetadata?: Record<string, unknown> & {
    organizations?: OrganizationMetadata[]
    jobTitle?: string | null
  }
}

interface AuthSession {
  id: string
  attemptFirstFactorVerification?: (params: {
    password: string
    strategy: 'password'
  }) => Promise<{ status?: string }>
  startVerification?: (params: {
    level: 'first_factor' | 'second_factor' | 'multi_factor'
  }) => Promise<{
    status?: string
    supportedFirstFactors?: { strategy: string }[] | null
  }>
}

export interface AuthContextType {
  user: AuthUser | null
  session: AuthSession | null
  loading: boolean
  isLoaded: boolean
  isSignedIn: boolean | undefined
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>
  requestPasswordReset: (email: string) => Promise<{ error: Error | null }>
  resetPassword: (
    password: string,
    code: string
  ) => Promise<{ error: Error | null }>
  signUp: (
    email: string,
    password: string,
    firstName?: string,
    lastName?: string,
    termsAccepted?: boolean
  ) => Promise<{
    error: Error | null
    needsVerification?: boolean
    email?: string
  }>
  signUpWithInvitation: (
    token: string,
    password: string,
    firstName?: string,
    lastName?: string,
    termsAccepted?: boolean
  ) => Promise<{
    error: Error | null
    needsVerification?: boolean
    email?: string
  }>
  signOut: () => Promise<{ error: Error | null }>
  showMigrationAlert: boolean
  setShowMigrationAlert: (show: boolean) => void
  getToken: (options?: {
    skipCache?: boolean
    organizationId?: string
  }) => Promise<string | null>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

interface ClerkError {
  errors?: Array<{
    code?: string
    message?: string
  }>
  message?: string
}

const isClerkError = (error: unknown): error is ClerkError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    ('errors' in error || 'message' in error)
  )
}

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message
  }

  if (isClerkError(error)) {
    return error.errors?.[0]?.message || error.message || 'An error occurred'
  }

  return 'An error occurred'
}

const isNetworkError = (error: unknown): boolean => {
  const lowerErrorMessage = getErrorMessage(error).toLowerCase()

  return (
    lowerErrorMessage.includes('failed to fetch') ||
    lowerErrorMessage.includes('network') ||
    lowerErrorMessage.includes('offline') ||
    lowerErrorMessage.includes('timeout') ||
    lowerErrorMessage.includes('econnrefused') ||
    lowerErrorMessage.includes('err_network')
  )
}

type SignInErrorKind = 'email-not-found' | 'invalid-password' | 'generic'

const isEnumerationError = (error: unknown): boolean => {
  if (!isClerkError(error)) {
    return false
  }

  const errorCode = error.errors?.[0]?.code
  const enumerationCodes: string[] = ['form_identifier_not_found']

  return enumerationCodes.includes(errorCode || '')
}

const getSignInErrorKind = (error: unknown): SignInErrorKind => {
  if (!isClerkError(error)) {
    return 'generic'
  }

  const errorCode = error.errors?.[0]?.code

  if (errorCode === 'form_identifier_not_found') {
    return 'email-not-found'
  }

  if (errorCode === 'form_password_incorrect') {
    return 'invalid-password'
  }

  return 'generic'
}

export const AuthProvider = ({ children }: Readonly<AuthProviderProps>) => {
  const [showMigrationAlert, setShowMigrationAlert] = useState(false)

  const { user } = useUser()
  const { session } = useSession()
  const {
    isLoaded,
    isSignedIn,
    signOut: clerkSignOut,
    getToken,
  } = useClerkAuth()
  const { signIn: clerkSignIn, isLoaded: signInLoaded } = useSignIn()
  const { signUp: clerkSignUp, isLoaded: signUpLoaded, setActive } = useSignUp()
  const { toast } = useToast()
  const loading = !isLoaded

  useEffect(() => {
    if (!isLoaded) {
      return
    }

    if (isSignedIn && user && getToken) {
      linkAnonymousConsentLogsToUser(user.id, getToken)
      postHogTrackingService.identifyUser(user as AuthUser)
      referralAttributionService.applyToPostHog()
    }

    if (!isSignedIn) {
      postHogTrackingService.reset()
    }
  }, [getToken, isLoaded, isSignedIn, user])

  const signIn = useCallback(
    async (email: string, password: string) => {
      if (!signInLoaded || !clerkSignIn) {
        return { error: new Error('Sign in not ready') }
      }

      try {
        setShowMigrationAlert(false)
        const result = await clerkSignIn.create({
          identifier: email,
          password: password,
        })

        if (result.status === 'complete') {
          window.location.href = '/'
          return { error: null }
        } else {
          return { error: new Error('Sign in incomplete') }
        }
      } catch (error: unknown) {
        const signInErrorKind = getSignInErrorKind(error)

        if (
          signInErrorKind === 'email-not-found' ||
          signInErrorKind === 'invalid-password'
        ) {
          return {
            error: new Error('Invalid email or password.'),
          }
        }

        return {
          error: new Error('Sign in failed. Please try again.'),
        }
      }
    },
    [signInLoaded, clerkSignIn]
  )

  const handlePasswordResetError = useCallback(
    (error: unknown) => {
      console.error('Password reset error:', error)

      if (isEnumerationError(error)) {
        toast({
          title: 'Password reset',
          description:
            'If an account exists with this email, you will receive a password reset code.',
        })
        return {
          error:
            error instanceof Error ? error : new Error(getErrorMessage(error)),
        }
      }

      if (isNetworkError(error)) {
        toast({
          variant: 'destructive',
          title: 'Password reset failed',
          description:
            'Unable to send password reset email. Please check your connection and try again.',
        })
        return {
          error:
            error instanceof Error ? error : new Error(getErrorMessage(error)),
        }
      }

      toast({
        variant: 'destructive',
        title: 'Password reset failed',
        description: 'Unable to send password reset email. Please try again.',
      })

      return {
        error:
          error instanceof Error ? error : new Error(getErrorMessage(error)),
      }
    },
    [toast]
  )

  const requestPasswordReset = useCallback(
    async (email: string) => {
      if (!signInLoaded || !clerkSignIn) {
        return { error: new Error('Sign in not ready') }
      }

      try {
        await clerkSignIn.create({
          strategy: 'reset_password_email_code',
          identifier: email,
        })

        toast({
          title: 'Password reset',
          description:
            'If an account exists with this email, you will receive a password reset code.',
        })

        return { error: null }
      } catch (error) {
        return handlePasswordResetError(error)
      }
    },
    [signInLoaded, clerkSignIn, toast, handlePasswordResetError]
  )

  const resetPassword = useCallback(
    async (password: string, code: string) => {
      if (!signInLoaded || !clerkSignIn) {
        return { error: new Error('Sign in not ready') }
      }

      try {
        const result = await clerkSignIn.attemptFirstFactor({
          strategy: 'reset_password_email_code',
          code,
          password,
        })

        if (setActive) {
          await setActive({ session: result.createdSessionId })
        }

        toast({
          title: 'Password reset',
          description: 'Your password has been reset successfully.',
        })

        return { error: null }
      } catch (error: unknown) {
        const message = getErrorMessage(error)

        toast({
          variant: 'destructive',
          title: 'Password reset failed',
          description: message,
        })

        console.error('Sign in error:', message)

        return {
          error:
            error instanceof Error ? error : new Error(getErrorMessage(error)),
        }
      }
    },
    [signInLoaded, clerkSignIn, toast, setActive]
  )

  const signUpWithInvitation = useCallback(
    async (
      clerkTicket: string,
      password: string,
      firstName?: string,
      lastName?: string,
      termsAccepted?: boolean
    ) => {
      if (!signUpLoaded || !clerkSignUp) {
        return { error: new Error('Sign up not ready') }
      }

      if (!termsAccepted) {
        toast({
          variant: 'destructive',
          title: 'Terms acceptance required',
          description:
            'You must accept the Terms & Conditions and Privacy Policy to sign up.',
        })
        return { error: new Error('Terms not accepted') }
      }

      try {
        const result = await clerkSignUp.create({
          strategy: 'ticket',
          ticket: clerkTicket,
          password,
          firstName: firstName || '',
          lastName: lastName || '',
        })

        if (result.status === 'complete') {
          toast({
            title: 'Account created successfully',
            description:
              'Welcome to Praxis! You can now start using the platform.',
          })

          await setActive({ session: result.createdSessionId })

          return { error: null }
        } else {
          return { error: new Error('Sign up incomplete') }
        }
      } catch (error: unknown) {
        toast({
          variant: 'destructive',
          title: 'Sign up failed',
          description: 'An error occurred while signing up.',
        })

        return {
          error:
            error instanceof Error ? error : new Error(getErrorMessage(error)),
        }
      }
    },
    [signUpLoaded, clerkSignUp, toast, setActive]
  )

  const signUp = useCallback(
    async (
      email: string,
      password: string,
      firstName?: string,
      lastName?: string,
      termsAccepted?: boolean
    ) => {
      if (!signUpLoaded || !clerkSignUp) {
        return { error: new Error('Sign up not ready') }
      }

      if (!termsAccepted) {
        toast({
          variant: 'destructive',
          title: 'Terms acceptance required',
          description:
            'You must accept the Terms & Conditions and Privacy Policy to sign up.',
        })
        return { error: new Error('Terms not accepted') }
      }

      try {
        const result = await clerkSignUp.create({
          emailAddress: email,
          password: password,
          firstName: firstName || '',
          lastName: lastName || '',
        })

        if (result.status === 'complete') {
          toast({
            title: 'Account created successfully',
            description:
              'Welcome to Praxis! You can now start using the platform.',
          })

          return { error: null }
        } else if (result.status === 'missing_requirements') {
          await clerkSignUp.prepareEmailAddressVerification({
            strategy: 'email_code',
          })

          return { error: null, needsVerification: true, email }
        } else {
          return { error: new Error('Sign up incomplete') }
        }
      } catch (error: unknown) {
        console.error('Sign up error:', error)

        toast({
          variant: 'destructive',
          title: 'Sign up failed',
          description:
            'Unable to create account. Please try again or sign in if you already have an account.',
        })

        return {
          error: new Error('Unable to create account'),
        }
      }
    },

    [signUpLoaded, clerkSignUp, toast]
  )

  const signOut = useCallback(async () => {
    try {
      postHogTrackingService.reset()
      clearStellaSessionStorage()

      await clerkSignOut()
      return { error: null }
    } catch (error: unknown) {
      toast({
        variant: 'destructive',
        title: 'Sign out failed',
        description: getErrorMessage(error),
      })

      return {
        error:
          error instanceof Error ? error : new Error(getErrorMessage(error)),
      }
    }
  }, [clerkSignOut, toast])

  const getTokenWithOptions = useCallback(
    async (options?: { skipCache?: boolean; organizationId?: string }) => {
      return getToken(options)
    },
    [getToken]
  )

  const value = useMemo(
    () => ({
      user: user as unknown as AuthUser | null,
      session: session as AuthSession | null,
      loading,
      isLoaded,
      isSignedIn,
      signIn,
      requestPasswordReset,
      resetPassword,
      signUpWithInvitation,
      signUp,
      signOut,
      showMigrationAlert,
      setShowMigrationAlert,
      getToken: getTokenWithOptions,
    }),
    [
      user,
      session,
      loading,
      isLoaded,
      isSignedIn,
      signIn,
      requestPasswordReset,
      resetPassword,
      signUpWithInvitation,
      signUp,
      signOut,
      showMigrationAlert,
      getTokenWithOptions,
    ]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        'useAuth called outside of AuthProvider. Returning safe fallbacks.'
      )
    }
    const fallback: AuthContextType = {
      user: null,
      session: null,
      loading: true,
      isLoaded: false,
      isSignedIn: false,
      signIn: async () => ({ error: new Error('AuthProvider missing') }),
      requestPasswordReset: async () => ({
        error: new Error('AuthProvider missing'),
      }),
      resetPassword: async () => ({
        error: new Error('AuthProvider missing'),
      }),
      signUp: async () => ({ error: new Error('AuthProvider missing') }),
      signUpWithInvitation: async () => ({
        error: new Error('AuthProvider missing'),
      }),
      signOut: async () => ({ error: new Error('AuthProvider missing') }),
      showMigrationAlert: false,
      setShowMigrationAlert: () => {},
      getToken: async () => null,
    }
    return fallback
  }
  return context
}
